import { formatAttendance, getSummativeCompetency, summativeCompetencyKey } from './summativeCompetency';

export const downloadConsolidatedSummativeMarksheetPdf = async ({ institution, group }) => {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = 297;
  const pageHeight = 210;
  const left = 10;
  const right = 287;
  const center = pageWidth / 2;
  let y = 10;

  if (institution?.logo_url) {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = institution.logo_url;
      await new Promise(resolve => { img.onload = resolve; img.onerror = resolve; });
      if (img.width && img.height) {
        const h = 16;
        const w = (img.width / img.height) * h;
        pdf.addImage(img, 'PNG', center - w / 2, y, w, h);
        y += h + 4;
      }
    } catch (_) { /* logo is optional */ }
  }

  pdf.setFont('times', 'bold');
  pdf.setFontSize(13);
  pdf.text((institution?.name || 'Institution').toUpperCase(), center, y, { align: 'center' });
  y += 5.5;
  pdf.setFontSize(11);
  pdf.text('SUMMATIVE EXAMINATION CONSOLIDATED MARKSHEET', center, y, { align: 'center' });
  y += 5;
  pdf.setFontSize(9);
  pdf.text(`${group?.programName || 'Programme'} - Level ${group?.programLevel || '-'} - Module ${group?.moduleNumber || '-'}`, center, y, { align: 'center' });
  y += 4.5;
  pdf.text(group?.period || '', center, y, { align: 'center' });
  y += 6;

  const units = group?.units || [];
  const students = group?.students || [];
  const tableWidth = right - left;
  const fixedWidths = [11, 38, 48];
  const fixedTotal = fixedWidths.reduce((sum, width) => sum + width, 0);
  const unitBlockWidth = units.length ? (tableWidth - fixedTotal) / units.length : tableWidth - fixedTotal;
  const fixedX = [left, left + fixedWidths[0], left + fixedWidths[0] + fixedWidths[1]];
  const unitX = units.map((_, index) => left + fixedTotal + (index * unitBlockWidth));
  const unitHeaderHeight = 7;
  const marksHeaderHeight = 7;
  const headerHeight = unitHeaderHeight + marksHeaderHeight;
  const rowHeight = 8;
  const tableRight = left + fixedTotal + (unitBlockWidth * units.length);
  const unitSubWidths = [unitBlockWidth * 0.16, unitBlockWidth * 0.16, unitBlockWidth * 0.16, unitBlockWidth * 0.16, unitBlockWidth * 0.36];
  // Keep the grid readable when many units are shown: cell rules are subtle,
  // while the boundary around each unit block remains visually prominent.
  const outerLineWidth = 0.8;
  const innerLineWidth = 0.2;
  const rowLineWidth = 0.2;

  const drawTableHeader = startY => {
    const unitsStartX = left + fixedTotal;
    pdf.setFillColor(225, 230, 238);
    pdf.rect(left, startY, tableRight - left, headerHeight, 'F');
    pdf.setDrawColor(20, 30, 45);
    pdf.setLineWidth(outerLineWidth);
    pdf.rect(left, startY, tableRight - left, headerHeight);
    fixedWidths.forEach((width, index) => {
      pdf.setLineWidth(outerLineWidth);
      pdf.line(fixedX[index] + width, startY, fixedX[index] + width, startY + headerHeight);
    });

    // Student columns are row-spanned. The module is already identified in
    // the document heading, so the table starts directly with unit codes.
    pdf.setDrawColor(175, 185, 198);
    pdf.setLineWidth(innerLineWidth);
    pdf.line(unitsStartX, startY + unitHeaderHeight, tableRight, startY + unitHeaderHeight);

    pdf.setFont('times', 'bold');
    pdf.setFontSize(8);
    ['SN', 'Registration No.', 'Student Name'].forEach((label, index) => {
      pdf.text(label, fixedX[index] + fixedWidths[index] / 2, startY + 8.5, { align: 'center', maxWidth: fixedWidths[index] - 2 });
    });

    // Unit boundaries are visually prominent and continue through both unit
    // header rows; the module itself is shown in the document heading above.
    pdf.setDrawColor(20, 30, 45);
    pdf.setLineWidth(outerLineWidth);
    pdf.line(unitsStartX, startY, unitsStartX, startY + headerHeight);
    pdf.line(tableRight, startY, tableRight, startY + headerHeight);

    units.forEach((unit, index) => {
      const x = unitX[index];
      const marksWidth = unitSubWidths.slice(0, 4).reduce((sum, width) => sum + width, 0);
      pdf.setDrawColor(20, 30, 45);
      pdf.setLineWidth(outerLineWidth);
      pdf.line(x + unitBlockWidth, startY, x + unitBlockWidth, startY + headerHeight);
      pdf.setFontSize(unitBlockWidth < 30 ? 6.5 : 8);
      pdf.text(unit.code || 'Unit', x + unitBlockWidth / 2, startY + 4.8, { align: 'center', maxWidth: unitBlockWidth - 2 });

      pdf.setDrawColor(175, 185, 198);
      pdf.setLineWidth(innerLineWidth);
      let subX = x;
      ['FA/60', 'SA/40', 'TT/100', 'AT%'].forEach((label, subIndex) => {
        if (subIndex > 0) pdf.line(subX, startY + unitHeaderHeight, subX, startY + headerHeight);
        pdf.setFontSize(unitBlockWidth < 30 ? 5.5 : 7);
        pdf.text(label, subX + unitSubWidths[subIndex] / 2, startY + unitHeaderHeight + 4.8, { align: 'center', maxWidth: unitSubWidths[subIndex] - 1 });
        subX += unitSubWidths[subIndex];
      });
      pdf.text('REMARK', x + marksWidth + unitSubWidths[4] / 2, startY + unitHeaderHeight + 4.8, { align: 'center', maxWidth: unitSubWidths[4] - 1 });
      pdf.line(x + marksWidth, startY + unitHeaderHeight, x + marksWidth, startY + headerHeight);
    });
    pdf.setDrawColor(20, 30, 45);
    pdf.setLineWidth(outerLineWidth);
    pdf.line(left, startY + headerHeight, tableRight, startY + headerHeight);
    return startY + headerHeight;
  };

  const drawStudentRow = (student, index, startY) => {
    pdf.setFont('times', 'normal');
    pdf.setFontSize(7.5);
    const fixedValues = [String(index + 1), student.admissionNumber || '-', student.name || 'Unknown student'];
    fixedValues.forEach((value, columnIndex) => pdf.text(String(value), fixedX[columnIndex] + fixedWidths[columnIndex] / 2, startY + 5.5, { align: 'center', maxWidth: fixedWidths[columnIndex] - 2 }));
    // Carry the fixed-column and table edges through the body as well as the
    // header; otherwise the light row rules can make the grid look broken.
    pdf.setDrawColor(20, 30, 45);
    pdf.setLineWidth(outerLineWidth);
    pdf.line(left, startY, left, startY + rowHeight);
    fixedWidths.forEach((width, columnIndex) => {
      pdf.setLineWidth(outerLineWidth);
      pdf.line(fixedX[columnIndex] + width, startY, fixedX[columnIndex] + width, startY + rowHeight);
    });
    units.forEach((unit, unitIndex) => {
      const mark = student.marks?.get(unit.key);
      const result = mark ? getSummativeCompetency(mark) : null;
      const values = result
        ? [String(result.formative), String(result.summative), String(result.total), `${formatAttendance(result.attendance)}%`, result.remark]
        : ['-', '-', '-', '-', '-'];
      let cellX = unitX[unitIndex];
      pdf.setDrawColor(20, 30, 45);
      pdf.setLineWidth(outerLineWidth);
      pdf.line(cellX, startY, cellX, startY + rowHeight);
      pdf.setDrawColor(175, 185, 198);
      values.forEach((value, subIndex) => {
        if (subIndex > 0) {
          pdf.setLineWidth(innerLineWidth);
          pdf.line(cellX, startY, cellX, startY + rowHeight);
        }
        pdf.text(String(value), cellX + unitSubWidths[subIndex] / 2, startY + 5.5, { align: 'center', maxWidth: unitSubWidths[subIndex] - 1 });
        cellX += unitSubWidths[subIndex];
      });
      pdf.setDrawColor(20, 30, 45);
      pdf.setLineWidth(outerLineWidth);
      pdf.line(unitX[unitIndex] + unitBlockWidth, startY, unitX[unitIndex] + unitBlockWidth, startY + rowHeight);
    });
    pdf.setLineWidth(outerLineWidth);
    pdf.line(tableRight, startY, tableRight, startY + rowHeight);
    pdf.setDrawColor(175, 185, 198);
    pdf.setLineWidth(rowLineWidth);
    pdf.line(left, startY + rowHeight, tableRight, startY + rowHeight);
  };

  y = drawTableHeader(y);
  students.forEach((student, index) => {
    if (y + rowHeight > pageHeight - 25) {
      pdf.addPage();
      y = drawTableHeader(12);
    }
    drawStudentRow(student, index, y);
    y += rowHeight;
  });
  if (!students.length) {
    pdf.setFont('times', 'normal');
    pdf.setFontSize(8);
    pdf.text('No trainer-verified marks are available.', center, y + 5.5, { align: 'center' });
    y += rowHeight;
  }

  y += 7;
  pdf.setFont('times', 'bold');
  pdf.setFontSize(6.2);
  pdf.text(summativeCompetencyKey, center, y, { align: 'center', maxWidth: tableWidth });
  y += 6;
  pdf.setFont('times', 'italic');
  pdf.setFontSize(7.5);
  pdf.text('Approval status: ' + ([...new Set((group?.batches || []).map(batch => batch.status))].join(', ') || 'pending'), center, y, { align: 'center' });
  const safe = String(group?.programName || 'programme').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
  pdf.save(`summative-consolidated-marksheet-${safe}-${group?.moduleNumber || 'module'}.pdf`);
};
